import {
  afterNextRender,
  Component,
  effect,
  ElementRef,
  HostListener,
  inject,
  Injector,
  OnInit,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmationService } from 'primeng/api';
import { SwitchLanguageComponent } from '../../../components/switch-language/switch-language.component';
import { NavigationEnd, RouterModule, Router } from '@angular/router';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthenticationService, UserContext, UserContextService } from '../../../../common';
import { CommonModule } from '@angular/common';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { SkeletonModule } from 'primeng/skeleton';
import { filter } from 'rxjs';
import { MobileMenuService } from '../../../services/mobile-menu.service';
import { HEADER_ACCOUNT_NAV, PROFILE_ROUTE } from './menu/account-menu';
import {
  HEADER_NAV,
  HeaderNavItem,
  HeaderNavLink,
  isHeaderNavGroup,
  isNavItemActive,
  isNavLinkActive,
  isUnderRoute
} from './menu/header-menu';
import { WalletStateService } from '../../../../financial-management/wallets';
import { RiyalSymbolComponent } from '../riyal-symbol/riyal-symbol.component';
import { NotificationBillComponent } from '../../../../common/utility-management/notifications/components/notification-bill/notification-bill.component';

/**
 * Width (px) at which the header switches between the desktop navigation and the drawer.
 * Keep in sync with `$header-desktop-min` in app-header.component.scss.
 */
const DESKTOP_NAV_MIN_WIDTH = 992;

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    SwitchLanguageComponent,
    RouterModule,
    ConfirmDialogModule,
    TranslateModule,
    CommonModule,
    NgbDropdownModule,
    SkeletonModule,
    RiyalSymbolComponent,
    CdkTrapFocus,
    NotificationBillComponent
  ],
  templateUrl: './app-header.component.html',
  styleUrl: './app-header.component.scss',
  providers: [ConfirmationService]
})
export class AppHeaderComponent implements OnInit {
  translate = inject(TranslateService);
  confirmationService = inject(ConfirmationService);
  authService = inject(AuthenticationService);
  mobileMenuService = inject(MobileMenuService);
  router = inject(Router);
  private walletState = inject(WalletStateService);
  private injector = inject(Injector);
  profileData: UserContext | undefined;
  wallet = this.walletState.wallet;
  walletResolved = this.walletState.isResolved;
  isAuthenticated = UserContextService.isAuthenticated();
  isMobileMenuOpen = this.mobileMenuService.isMobileMenuOpen;

  readonly navItems = HEADER_NAV;
  readonly accountNav = HEADER_ACCOUNT_NAV;
  readonly profileRoute = PROFILE_ROUTE;
  readonly isGroup = isHeaderNavGroup;

  // The URL the active states are computed from (see menu/header-menu.ts). Updated on every navigation.
  private currentUrl = signal(this.router.url);

  private menuToggle = viewChild<ElementRef<HTMLButtonElement>>('menuToggle');
  private drawerClose = viewChild<ElementRef<HTMLButtonElement>>('drawerClose');
  private drawerWasOpen = false;

  constructor() {
    // Any navigation refreshes the active states and closes the drawer. (Clicking a link to the page you are already
    // on does not navigate, so the links also close it on click.)
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe((event) => {
        this.currentUrl.set(event.urlAfterRedirects);
        this.closeMobileMenu();
      });

    // Focus follows the drawer: into it when it opens, back to the menu button when it closes.
    effect(() => {
      const open = this.isMobileMenuOpen();
      untracked(() => this.moveFocusForDrawer(open));
    });
  }

  ngOnInit(): void {
    if (!this.isAuthenticated) {
      return;
    }

    this.profileData = UserContextService.getUserContext();
    // Inside the layout BaseComponent has already started (or finished) this load, so this is a no-op there. It
    // only does work where the header is embedded outside the layout (ticket pages).
    this.walletState.ensureWalletLoaded();
  }

  @HostListener('window:resize')
  onResize() {
    if (window.innerWidth >= DESKTOP_NAV_MIN_WIDTH) {
      this.closeMobileMenu();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    if (this.isMobileMenuOpen()) {
      this.closeMobileMenu();
    }
  }

  /** Active state of a primary item: a link is active on its own pages, a group when any of its children is. */
  isActive(item: HeaderNavItem): boolean {
    return isNavItemActive(this.currentUrl(), item);
  }

  isLinkActive(link: HeaderNavLink): boolean {
    return isNavLinkActive(this.currentUrl(), link);
  }

  isProfileActive(): boolean {
    return isUnderRoute(this.currentUrl(), PROFILE_ROUTE);
  }

  navigateToWallet(): void {
    this.closeMobileMenu();
    this.router.navigate(['/my-wallet']);
  }

  logout() {
    this.walletState.clear();
    this.authService.logout();
  }

  confirmLogOut(event: Event) {
    this.confirmationService.confirm({
      header: this.translate.instant('GENERAL.LOGOUT_CONFIRMATION'),
      message: this.translate.instant('GENERAL.CONFIRM_TO_PROCEED'),
      accept: () => {
        this.logout();
      },
      reject: () => {}
    });
  }

  toggleMobileMenu() {
    this.mobileMenuService.toggleMobileMenu();
  }

  closeMobileMenu() {
    this.mobileMenuService.closeMobileMenu();
  }

  navigateToProfile() {
    this.closeMobileMenu();
    this.router.navigate([PROFILE_ROUTE]);
  }

  navigateToLogin(): void {
    this.closeMobileMenu();
    this.router.navigate(['/auth/login']);
  }

  private moveFocusForDrawer(open: boolean): void {
    if (open === this.drawerWasOpen) {
      return;
    }
    this.drawerWasOpen = open;

    if (open) {
      // The drawer is `inert` until this change-detection pass finishes, so wait for the render before focusing.
      afterNextRender(() => this.drawerClose()?.nativeElement.focus(), { injector: this.injector });
    } else {
      this.menuToggle()?.nativeElement.focus();
    }
  }
}
